import React, { useCallback, useMemo } from "react";
import { Entity } from "@common/types/entity";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { ConsolidatedAvatar } from "@components/avatar/ConsolidatedAvatar";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { EntityDisplay } from "@components/entity/EntityDisplay";
import styles from "./ConsolidatedEntitySelect.module.scss";

const CONSOLIDATED_VALUE = "consolidated";

interface Props {
    entities: Entity[];
    selectedEntity?: Entity;
    isConsolidated: boolean;
    consolidatedLabel: string;
    onSelectEntity: (entity: Entity | undefined) => void;
    onSelectConsolidated: () => void;
    disabled?: boolean;
    className?: string;
}

/**
 * Entity selector with an extra "All entities (Consolidated)" option used by the
 * P&L / Balance Sheet report toolbars. Picking the consolidated option switches
 * the report to the consolidation group layout; picking an entity returns to the
 * standard single-entity report.
 */
export const ConsolidatedEntitySelect: React.FC<Props> = ({
    entities,
    selectedEntity,
    isConsolidated,
    consolidatedLabel,
    onSelectEntity,
    onSelectConsolidated,
    disabled = false,
    className,
}) => {
    const value = isConsolidated
        ? CONSOLIDATED_VALUE
        : (selectedEntity?.id.toString() ?? null);

    const items = useMemo<Array<PickerItemData<string>>>(
        () => [
            {
                value: CONSOLIDATED_VALUE,
                label: consolidatedLabel,
                leading: (
                    <ConsolidatedAvatar
                        name={consolidatedLabel}
                        size="xs"
                        aria-hidden
                    />
                ),
            },
            ...entities.map((entity) => ({
                value: entity.id.toString(),
                label: entity.name ?? "",
                leading: <EntityAvatar entity={entity} size="xs" aria-hidden />,
            })),
        ],
        [consolidatedLabel, entities],
    );

    const onValueChange = useCallback(
        (next: string | null) => {
            if (next === CONSOLIDATED_VALUE) {
                onSelectConsolidated();
                return;
            }
            onSelectEntity(
                entities.find((entity) => entity.id.toString() === next),
            );
        },
        [entities, onSelectConsolidated, onSelectEntity],
    );

    const triggerValue = useMemo(() => {
        if (isConsolidated) {
            return (
                <div className={styles.consolidatedOption}>
                    <ConsolidatedAvatar
                        name={consolidatedLabel}
                        size="xs"
                        aria-hidden
                    />
                    <span className={styles.name}>{consolidatedLabel}</span>
                </div>
            );
        }
        return selectedEntity ? (
            <EntityDisplay entity={selectedEntity} />
        ) : undefined;
    }, [isConsolidated, consolidatedLabel, selectedEntity]);

    return (
        <div className={className}>
            <Picker.Select
                items={items}
                value={value}
                onValueChange={onValueChange}
                disabled={disabled}
                placeholder="Select entity"
                renderValue={() => triggerValue}
            />
        </div>
    );
};
